import type { EmployerResource } from 'util/resources/Employer';
import type { EmployerInterviewEventTypeResource } from 'util/resources/EmployerInterviewEventType';
import type { EmployerProfileResource } from 'util/resources/EmployerProfile';
import type { InterviewEventTypeResource } from 'util/resources/InterviewEventType';
import type { JobListingResource } from 'util/resources/JobListing';
import type { UserProfileResource } from 'util/resources/UserProfile';

/**
 * This method should only be used when the URL string is required.
 * For example, when we wish to copy a URL to the clipboard.
 * Otherwise, use the Link component from next/link with the `/` prefix for
 * absolute paths from the origin.
 */
export const fullUrl = (path: string): string => {
  if (!path) {
    throw new Error('Path is required to generate URL');
  }
  if (typeof window === 'undefined') {
    return path;
  }
  return new URL(path, window.location.origin).toString();
};

export const jobUrl = (job: JobListingResource): string => {
  if (!job?.slug) {
    throw new Error('Job slug is required to generate URL');
  }
  return `/jobs/${job.slug}`;
};

export const employerJobUrl = (job: JobListingResource): string => {
  if (!job?.slug) {
    throw new Error('Job slug is required to generate URL');
  }
  return `/employer/jobs/${job.slug}`;
};

export const employerUrl = (employer: EmployerResource): string => {
  if (!employer?.slug) {
    throw new Error('Employer slug is required to generate URL');
  }
  return `/employer/${employer.slug}`;
};

export const userProfileLikeUrl = (userProfileId: string): string => {
  if (!userProfileId) {
    throw new Error('UserProfile Id is required to generate URL');
  }
  return `/employer/user_profiles/${userProfileId}/like`;
};

export const jobListingLikeUrl = (jobListingId: string): string => {
  if (!jobListingId) {
    throw new Error('Job Id is required to generate URL');
  }
  return `/job_listings/${jobListingId}/like`;
};

export const applicantURL = (applicationId: string): string => {
  if (!applicationId) {
    throw new Error('Application Id is required to generate URL');
  }
  return `/employer/applicants/${applicationId}`;
};

export const educatorUrl = (educator: UserProfileResource) => {
  if (!educator?.slug) {
    throw new Error('Educator slug is required to generate URL');
  }
  return `/educators/${educator.slug}`;
};

export const externalVideoAskUrl = ({
  email,
  name,
  medium = 'banner',
}: {
  email: string;
  name: string;
  medium?: string;
}): string => {
  return `https://video.earlyday.com/f6axgeiy7#contact_email=${email}&contact_name=${encodeURIComponent(
    name,
  )}&medium=${medium}`;
};

export const eventSchedulingUrl = (
  eventType: InterviewEventTypeResource | EmployerInterviewEventTypeResource,
  employerProfile: EmployerProfileResource,
) => `/interview/${employerProfile.slug}/${eventType.slug}`;

/**
 * Static routes for the application.
 */
enum EarlyDayRoutesEnum {
  CAREER_RESOURCES = 'CAREER_RESOURCES',
  CONTACT_US = 'CONTACT_US',
  FACEBOOK = 'FACEBOOK',
  HOME = 'HOME',
  INSTAGRAM = 'INSTAGRAM',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  HELP = 'HELP',
}

export const EarlyDayRoutes: Record<EarlyDayRoutesEnum, string> = {
  CAREER_RESOURCES: 'https://www.earlyday.com/blog-category/educators',
  CONTACT_US: 'https://www.earlyday.com/contact-us',
  FACEBOOK: 'https://www.facebook.com/joinearlyday',
  HOME: 'https://www.earlyday.com',
  INSTAGRAM: 'https://www.instagram.com/joinearlyday',
  PRIVACY_POLICY: 'https://www.earlyday.com/privacy-policy',
  HELP: 'https://help.earlyday.com',
};

enum AppRoutesEnum {
  EDUCATOR_APPLICATIONS = 'EDUCATOR_APPLICATIONS',
  EDUCATOR_INTERVIEWS = 'EDUCATOR_INTERVIEWS',
  EDUCATOR_MESSAGES = 'EDUCATOR_MESSAGES',
  EDUCATOR_ONBOARDING = 'EDUCATOR_ONBOARDING',
  EDUCATOR_PROFILE = 'EDUCATOR_PROFILE',
  EDUCATOR_SETTINGS = 'EDUCATOR_SETTINGS',
  EMPLOYER_APPLICANTS = 'EMPLOYER_APPLICANTS',
  EMPLOYER_INTERVIEWS = 'EMPLOYER_INTERVIEWS',
  EMPLOYER_INTERVIEWS_AVAILABILITY = 'EMPLOYER_INTERVIEWS_AVAILABILITY',
  EMPLOYER_INTERVIEWS_EVENT_TYPES = 'EMPLOYER_INTERVIEWS_EVENT_TYPES',
  EMPLOYER_JOBS = 'EMPLOYER_JOBS',
  EMPLOYER_MESSAGES = 'EMPLOYER_MESSAGES',
  EMPLOYER_NEW_JOB = 'EMPLOYER_NEW_JOB',
  EMPLOYER_PLANS = 'EMPLOYER_PLANS',
  EMPLOYER_PROFILE = 'EMPLOYER_PROFILE',
  EMPLOYER_PROFILE_CHANGE = 'EMPLOYER_PROFILE_CHANGE',
  EMPLOYER_PROFILE_PREFERENCES = 'EMPLOYER_PROFILE_PREFERENCES',
  EMPLOYER_UPGRADE = 'EMPLOYER_UPGRADE',
  EMPLOYER_DOWNGRADE = 'EMPLOYER_DOWNGRADE',
  HIRING_HEROS_LANDING_PAGE = 'HIRING_HEROS_LANDING_PAGE',
  EMPLOYER_HIRING_HERO_STATS_PAGE = 'EMPLOYER_HIRING_HERO_STATS_PAGE',
  LOG_IN = 'LOG_IN',
  REGISTER_EDUCATOR = 'REGISTER_EDUCATOR',
  REGISTER_EDUCATOR_CITY_PENDING = 'REGISTER_EDUCATOR_CITY_PENDING',
  REGISTER_EDUCATOR_LOCATION = 'REGISTER_EDUCATOR_LOCATION',
  REGISTER_EMPLOYER = 'REGISTER_EMPLOYER',
  RESET_PASSWORD = 'RESET_PASSWORD',
  SEARCH_EDUCATORS = 'SEARCH_EDUCATORS',
  SEARCH_JOBS = 'SEARCH_JOBS',
  SIGN_UP = 'SIGN_UP',
  EMPLOYER_PENDING_LAUNCH = 'EMPLOYER_PENDING_LAUNCH',
}

export const AppRoutes: Record<AppRoutesEnum, string> = {
  EDUCATOR_APPLICATIONS: '/jobs/applications',
  EDUCATOR_INTERVIEWS: '/interviews',
  EDUCATOR_MESSAGES: '/messages',
  EDUCATOR_ONBOARDING: '/register/teacher/onboarding',
  EDUCATOR_PROFILE: '/profile',
  EDUCATOR_SETTINGS: '/settings',
  EMPLOYER_APPLICANTS: '/employer/applicants',
  EMPLOYER_INTERVIEWS: '/employer/interviews',
  EMPLOYER_INTERVIEWS_AVAILABILITY: '/employer/interviews?tab=availability',
  EMPLOYER_INTERVIEWS_EVENT_TYPES: '/employer/interviews?tab=event-types',
  EMPLOYER_JOBS: '/employer/jobs',
  EMPLOYER_MESSAGES: '/employer/messages',
  EMPLOYER_NEW_JOB: '/employer/jobs/new',
  EMPLOYER_PLANS: '/employer/plans',
  EMPLOYER_PROFILE: '/employer/profile',
  EMPLOYER_PROFILE_CHANGE: '/select-employer-profile',
  EMPLOYER_PROFILE_PREFERENCES: '/employer/profile#preferences',
  EMPLOYER_UPGRADE: '/employer/upgrade',
  EMPLOYER_DOWNGRADE: '/employer/downgrade',
  EMPLOYER_HIRING_HERO_STATS_PAGE: '/employer/hiring-hero-stats',
  HIRING_HEROS_LANDING_PAGE: '/hiring-hero',
  LOG_IN: '/login',
  REGISTER_EDUCATOR: '/register/teacher',
  REGISTER_EDUCATOR_CITY_PENDING: '/register/teacher/city_pending',
  REGISTER_EDUCATOR_LOCATION: '/register/teacher/locations',
  REGISTER_EMPLOYER: '/register/employer',
  RESET_PASSWORD: '/reset_password',
  SEARCH_EDUCATORS: '/educators',
  SEARCH_JOBS: '/jobs',
  SIGN_UP: '/sign_up',
  EMPLOYER_PENDING_LAUNCH: '/register/employer/other',
};
